import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { Language, LifeContext } from '../types';
import { UI_STRINGS, UIStrings } from '../constants/translations';

interface LanguageContextType {
  language: Language;
  t: UIStrings;
  getLocale: () => string;
  getWhisperLang: () => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
  language: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, language }) => {
  const t = useMemo(() => UI_STRINGS[language] || UI_STRINGS.English, [language]);
  
  const getLocale = () => {
    const localeMap: Record<Language, string> = {
      Italian: 'it-IT',
      English: 'en-US',
      Spanish: 'es-ES',
      German: 'de-DE',
      French: 'fr-FR'
    };
    return localeMap[language] || 'en-US';
  };

  const getWhisperLang = () => {
    const whisperMap: Record<Language, string> = {
      Italian: 'it',
      English: 'en',
      Spanish: 'es',
      German: 'de',
      French: 'fr'
    };
    return whisperMap[language] || 'en';
  };

  return (
    <LanguageContext.Provider value={{ language, t, getLocale, getWhisperLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

export const useLanguage = (): Language => {
  const { language } = useTranslation();
  return language;
};
